<template lang="pug">
.curso-main-container.pb-3
  BannerInterno
  .container.tarjeta.tarjeta--blanca.p-4.p-md-5.mb-5
    .titulo-principal.color-acento-contenido(data-aos="flip-up")
      .titulo-principal__numero
        span 5
      h1 Planeación de actividades como elementos de estructuración 
    p.mb-5 La planeación de actividades constituye uno de los pilares fundamentales en la estructuración del proyecto. Aunque la planificación ya fue abordada como fase del ciclo de vida, en este apartado se profundiza en la organización detallada de las actividades como componentes estructurales que garantizan coherencia entre los objetivos y el propósito general.
    .caja-tema2.mb-5(data-aos="zoom-in")
      .row.align-items-center
        .col-xl-auto
          figure
            img(src='@/assets/curso/tema5/1.png', alt='').m-auto
        .col-xl
          p.mb-0 Estructurar actividades no significa únicamente enumerar tareas. Implica organizarlas de manera lógica, establecer relaciones de dependencia, asignar recursos adecuados y asegurar que cada acción contribuya de forma directa al cumplimiento de los objetivos definidos. En este sentido, la planeación permite transformar los objetivos en acciones concretas y medibles, fortaleciendo la coherencia interna del proyecto.
    p.mb-0 Cuando las actividades están correctamente estructuradas, el proyecto adquiere claridad operativa y facilita el seguimiento posterior. Por el contrario, una planeación imprecisa puede generar desorganización, duplicidad de esfuerzos y retrasos que afectan la ejecución y el logro de los resultados esperados.
    Separador
    #t_5_1.titulo-segundo.color-acento-contenido(data-aos="fade-right")
      h2 5.1. Organización secuencial de actividades
    p.mb-5 La organización secuencial implica establecer un orden lógico en el desarrollo de las actividades. No todas las acciones pueden ejecutarse simultáneamente; algunas dependen del resultado previo de otras. Esta relación de dependencia exige definir prioridades y determinar un flujo progresivo que responda a la lógica del proyecto y a sus objetivos específicos. Los principales aportes de la organización secuencial en la estructuración del proyecto:
    .bg--slider.mb-5
      .px-5
        .ajuste-slider(data-aos="zoom-in")
          SlyderA(tipo="b")
            .row.justify-content-center.align-items-center
              .col-xl-6.mb-4
                h4 Evita duplicidad de esfuerzos
                p.mb-0 Reduce la repetición innecesaria de tareas y optimiza el uso de recursos.
              .col-xl-6
                figure
                  img(src='@/assets/curso/tema5/2.png', alt='' style="max-width: 500px").m-auto
            .row.justify-content-center.align-items-center
              .col-xl-6.mb-4
                h4 Establece prioridades
                p.mb-0 Permite identificar qué actividades deben ejecutarse primero según su nivel de incidencia.
              .col-xl-6
                figure
                  img(src='@/assets/curso/tema5/3.png', alt='' style="max-width: 500px").m-auto
            .row.justify-content-center.align-items-center
              .col-xl-6.mb-4
                h4 Optimiza tiempos
                p.mb-0 Organiza el cronograma conforme a dependencias reales entre actividades.
              .col-xl-6
                figure
                  img(src='@/assets/curso/tema5/4.png', alt='' style="max-width: 500px").m-auto
            .row.justify-content-center.align-items-center
              .col-xl-6.mb-4
                h4 Facilita el control del avance
                p.mb-0 Permite realizar seguimiento progresivo y detectar retrasos oportunamente.
              .col-xl-6
                figure
                  img(src='@/assets/curso/tema5/5.png', alt='' style="max-width: 500px").m-auto
    p.mb-0 En consecuencia, la secuencia fortalece la estructura interna del proyecto y consolida su desarrollo como un proceso progresivo y articulado.
    Separador
    #t_5_2.titulo-segundo.color-acento-contenido(data-aos="fade-right")
      h2 5.2. División del proyecto en bloques
    p.mb-5 Dividir el proyecto en bloques o componentes facilita su comprensión y manejo operativo. Cada bloque agrupa actividades relacionadas que cumplen una función específica dentro del conjunto general, lo que permite abordar el proyecto por partes sin perder la coherencia con el propósito global. A continuación, se presentan los principales aportes de la organización por bloques:
    .row.justify-content-center.mb-5
      .col-xl-3.col-md-9(data-aos="zoom-in")
        .caja-tema5.h-100
          .row.justify-content-center.mb-3
            .col-7
              figure.mb-4(data-aos="fade-right")
                img(src='@/assets/curso/tema5/6.svg', alt="" style="max-width: 90px").m-auto
            h4.text-center Simplificación de la gestión
            p.mb-0.text-center Permite trabajar por componentes organizados y delimitados.
      .col-xl-3.col-md-9(data-aos="zoom-in").espacio___movil_3
        .caja-tema5.h-100
          .row.justify-content-center.mb-3
            .col-7
              figure.mb-4(data-aos="fade-right")
                img(src='@/assets/curso/tema5/7.svg', alt="" style="max-width: 90px").m-auto
            h4.text-center Asignación de responsabilidades claras
            p.mb-0.text-center Define responsables específicos por cada bloque.
      .col-xl-3.col-md-9(data-aos="zoom-in").espacio___movil_3
        .caja-tema5.h-100
          .row.justify-content-center.mb-3
            .col-7
              figure.mb-4(data-aos="fade-left")
                img(src='@/assets/curso/tema5/8.svg', alt="" style="max-width: 90px").m-auto
            h4.text-center Establecimiento de metas parciales
            p.mb-0.text-center Facilita el cumplimiento progresivo de resultados intermedios.
      .col-xl-3.col-md-9(data-aos="zoom-in").espacio___movil_3
        .caja-tema5.h-100
          .row.justify-content-center.mb-3
            .col-7
              figure.mb-4(data-aos="fade-left")
                img(src='@/assets/curso/tema5/9.svg', alt="" style="max-width: 90px").m-auto
            h4.text-center Seguimiento estructurado
            p.mb-0.text-center Permite evaluar avances por componente sin afectar la visión integral.
    p.mb-0 Por tanto, la organización en bloques contribuye a una estructuración sólida, ordenada y coherente, que integra las partes sin fragmentar el propósito general.
    Separador
    #t_5_3.titulo-segundo.color-acento-contenido(data-aos="fade-right")
      h2 5.3. Asignación de recursos por actividad
    p.mb-5 Cada actividad requiere recursos específicos para su ejecución. La asignación adecuada de estos recursos garantiza continuidad operativa y previene interrupciones que puedan afectar el desarrollo del proyecto. Los recursos pueden clasificarse de la siguiente manera:
    .row.align-items-center.mb-5
      .col-xl-4(data-aos="fade-right")
        figure.d-none.d-xl-block
          img(src='@/assets/curso/tema5/10.png', alt='' style="max-width: 350px").m-auto
      .col-xl-8(data-aos="fade-left")
        SlyderF(columnas="col-lg-6 col-xl-6")
          .tarjeta.color-acento-botones.ajuste--tarjetas-sinbordes-2
            .row.justify-content-center.mb-3
              .col-12
                img(src='@/assets/curso/tema5/11.png').m-auto
            h4.mb-3.estilo-text Humanos
            p.mb-0.text-center Personal responsable de la ejecución y coordinación de actividades.
          .tarjeta.color-acento-botones.ajuste--tarjetas-sinbordes-2
            .row.justify-content-center.mb-3
              .col-12
                img(src='@/assets/curso/tema5/12.png').m-auto
            h4.mb-3.estilo-text Técnicos
            p.mb-0.text-center Herramientas, equipos o plataformas necesarias para el desarrollo operativo.
          .tarjeta.color-acento-botones.ajuste--tarjetas-sinbordes-2
            .row.justify-content-center.mb-3
              .col-12
                img(src='@/assets/curso/tema5/13.png').m-auto
            h4.mb-3.estilo-text Materiales
            p.mb-0.text-center Insumos físicos requeridos para la ejecución.
          .tarjeta.color-acento-botones.ajuste--tarjetas-sinbordes-2
            .row.justify-content-center.mb-3
              .col-12
                img(src='@/assets/curso/tema5/14.png').m-auto
            h4.mb-3.estilo-text Financieros
            p.mb-0.text-center Presupuesto destinado al cumplimiento de cada actividad.
          .tarjeta.color-acento-botones.ajuste--tarjetas-sinbordes-2
            .row.justify-content-center.mb-3
              .col-12
                img(src='@/assets/curso/tema5/15.png').m-auto
            h4.mb-3.estilo-text Informativos
            p.mb-0.text-center Datos, documentos o fuentes necesarias para la toma de decisiones.
    p.mb-0 Asignar recursos por actividad permite establecer responsabilidades claras, prever necesidades operativas y asegurar la viabilidad técnica y financiera del proyecto.
    Separador
    #t_5_4.titulo-segundo.color-acento-contenido(data-aos="fade-right")
      h2 5.4. Relación entre planeación y cumplimiento del propósito
    p.mb-5 La planeación actúa como puente entre los objetivos específicos y el propósito general. Cuando las actividades están organizadas secuencialmente, agrupadas en bloques funcionales y cuentan con recursos asignados, el proyecto avanza con mayor coherencia estructural.
    .row.justify-content-center.mb-5
      .col-lg-10
        .row.justify-content-center
          .col-xl-4.col-md-9(data-aos="fade-right")
            .caja-tema5-1.h-100
              .row.justify-content-center.mb-3
                .col-12
                  figure.mb-4
                    img(src='@/assets/curso/tema5/16.png', alt="" ).m-auto
                h4.text-center Secuenciación lógica
                p.mb-0.text-center Garantiza desarrollo progresivo alineado con los objetivos.
          .col-xl-4.col-md-9(data-aos="zoom-in").espacio___movil_3
            .caja-tema5-1.h-100
              .row.justify-content-center.mb-3
                .col-12
                  figure.mb-4
                    img(src='@/assets/curso/tema5/17.png', alt="" ).m-auto
                h4.text-center Organización por bloques
                p.mb-0.text-center Permite integración estructurada de componentes.
          .col-xl-4.col-md-9(data-aos="fade-left").espacio___movil_3
            .caja-tema5-1.h-100
              .row.justify-content-center.mb-3
                .col-12
                  figure.mb-4
                    img(src='@/assets/curso/tema5/18.png', alt="" ).m-auto
                h4.text-center Asignación de recursos
                p.mb-0.text-center Asegura viabilidad operativa y sostenibilidad del proyecto.
    p.mb-0 En ausencia de una planeación adecuada, pueden generarse desviaciones que dificulten el logro del propósito general. Por ello, estructurar cuidadosamente las actividades no es un requisito formal, sino una condición esencial para alcanzar los resultados esperados con coherencia, eficiencia y sostenibilidad.
</template>

<script>
export default {
  name: 'Tema5',
  data: () => ({
    // variables de vue
  }),
  mounted() {
    this.$nextTick(() => {
      this.$aosRefresh()
    })
  },
  updated() {
    this.$aosRefresh()
  },
}
</script>

<style lang="sass"></style>
